<script setup lang="ts">
  import { MpCheckbox, MpContextMenu, MpRadio, MpSpinner, MpText } from '@borg/ui';
  import { MpFile } from '@borg/types';

  const props = withDefaults(
    defineProps<{
      file: MpFile;
      selectable?: boolean;
      multiSelectable?: boolean;
      disabled?: boolean;
      checked?: boolean;
      progress?: number;
      showSubtitle?: boolean;
      showContextMenu?: boolean;
    }>(),
    {
      progress: undefined,
      showSubtitle: true,
      showContextMenu: true,
    },
  );

  defineEmits<{
    toggle: [file: MpFile];
    delete: [file: MpFile];
    edit: [file: MpFile];
    preview: [file: MpFile];
  }>();

  defineSlots<{
    beforeTitle(props: Record<string, unknown>): void;
    afterTitle(props: Record<string, unknown>): void;
    title(props: { file: MpFile }): void;
    subtitle(props: { file: MpFile }): void;
  }>();

  const isLoading = computed(() => props.progress !== undefined && props.progress !== 100);
</script>

<template>
  <div class="file-card">
    <div class="file-card__aside">
      <div class="checkbox">
        <MpCheckbox
          v-if="selectable && multiSelectable"
          :model-value="!!checked"
          :disabled="disabled"
          @click="$emit('toggle', file)"
        />
        <MpRadio
          v-if="selectable && !multiSelectable"
          :value="file"
          :model-value="checked ? file : undefined"
          :disabled="disabled"
          @click="$emit('toggle', file)"
        />
      </div>
      <div
        v-if="$slots.beforeTitle"
        class="before-title"
      >
        <slot name="beforeTitle" />
      </div>
      <slot
        name="title"
        :file="file"
      >
        <MpText
          as="h6"
          class="file-card__file-name"
          no-margin
          no-wrap
          @click="$emit('preview', file)"
        >
          {{ file.name }}
        </MpText>
      </slot>
      <div
        v-if="$slots.afterTitle"
        class="after-title"
      >
        <slot name="afterTitle" />
      </div>
      <slot
        v-if="showSubtitle"
        name="subtitle"
        :file="file"
      >
        <MpText
          class="subtitle"
          as="div"
          no-margin
        >
          {{ $t(`DOCUMENTS_LIST.SOURCE.${file.source?.toUpperCase()}`) }},
          {{ $d(...localeDateTimeFormat(file.createdAt, 'dmy-time')) }}
        </MpText>
      </slot>
    </div>
    <MpSpinner
      v-if="isLoading"
      color="var(--mp-color-primary-default)"
    />
    <MpContextMenu
      v-if="showContextMenu && !isLoading"
      :options="[
        { label: $t('PREVIEW'), onClick: () => $emit('preview', file) },
        { label: $t('EDIT'), onClick: () => $emit('edit', file) },
        { label: $t('DELETE'), onClick: () => $emit('delete', file) },
      ]"
    />
  </div>
</template>

<style scoped lang="scss">
  $width: 100vw;

  .file-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--mp-space-50);
    padding: var(--mp-space-50) 0;

    .before-title {
      grid-area: before-title;
      display: flex;
      align-items: center;
    }

    .checkbox {
      grid-area: checkbox;
      display: flex;
      align-items: center;
    }

    .after-title {
      grid-area: after-title;
      display: flex;
      align-items: center;
    }

    .subtitle {
      grid-area: after-title;
    }

    &__aside {
      display: grid;
      gap: var(--mp-space-10) var(--mp-space-50);
      grid-template-columns: min-content 1fr;
      grid-template-areas:
        '. before-title'
        'checkbox title'
        'checkbox after-title';
    }

    &__text {
      cursor: pointer;
    }

    &__file-name {
      grid-area: title;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      cursor: pointer;
    }
  }

  @keyframes placeholder {
    0% {
      background-position: calc(0% - $width) 0;
    }
    100% {
      background-position: calc(100% + $width) 0;
    }
  }
</style>
